import apiService from './apiService';

// Types
export interface JournalEntryLine {
  id?: string;
  accountId: string;
  description?: string;
  debit: number;
  credit: number;
}

export interface JournalEntry {
  id?: string;
  entryNumber?: string;
  date: string;
  description: string;
  status?: string;
  lines: JournalEntryLine[];
  totalDebit?: number;
  totalCredit?: number;
}

export interface Account {
  id: string;
  accountNumber: string;
  name: string;
  balance?: number;
  currentBalance?: number;
  initialBalance?: number;
  type?: string;
  isActive?: boolean;
  createdAt?: string;
  parentAccount?: string | null;
  parentAccountName?: string | null;
  isReal?: boolean;  // Flag to indicate if this is a real account
  isGeneralAccount?: boolean;  // Flag to indicate if this is a general account
  level?: number;  // المستوى في الشجرة الهرمية
  children?: Account[];  // الحسابات الفرعية المباشرة
  isExpanded?: boolean;  // حالة العرض (موسع أو مطوي)
}

export interface VoucherTemplate {
  id: string;
  name: string;
  description?: string;
  templateType: 'receipt' | 'payment' | 'journal';
  debitAccount?: Account;
  creditAccount?: Account;
  debitAccounts?: Account[];
  creditAccounts?: Account[];
  isActive?: boolean;
  createdAt?: string;
}

export interface Transaction {
  id: string;
  sequentialId: number;  // رقم المعاملة التسلسلي
  date: string;
  referenceNumber: string;
  referenceType: 'سند قبض' | 'سند صرف' | 'قيد يومية';
  description: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  balance: number;
  notes: string; // حقل الملاحظات
  createdBy: string;
  isReal?: boolean;  // Flag to indicate if this is a real transaction
  balanceConfirmed?: boolean;  // حالة تأكيد الرصيد
  balanceConfirmedAt?: string;  // تاريخ تأكيد الرصيد
  balanceConfirmedBy?: string;  // المستخدم الذي أكد الرصيد
}

// Journal Entry Services
export const getJournalEntries = async (params?: any) => {
  try {
    return await apiService.get<JournalEntry[]>('/finance/journal-entries', { params });
  } catch (error) {
    console.error('Error fetching journal entries:', error);
    throw error;
  }
};

export const getJournalEntryById = async (id: string) => {
  try {
    return await apiService.get<JournalEntry>(`/finance/journal-entries/${id}`);
  } catch (error) {
    console.error(`Error fetching journal entry ${id}:`, error);
    throw error;
  }
};

export const createJournalEntry = async (entryData: JournalEntry) => {
  try {
    return await apiService.post<{ id: string; entryNumber: string; message: string }>('/finance/journal-entries', entryData);
  } catch (error) {
    console.error('Error creating journal entry:', error);
    throw error;
  }
};

export const updateJournalEntry = async (id: string, entryData: JournalEntry) => {
  try {
    // إضافة معلومات أكثر تفصيلاً للطلب
    console.log(`طلب تحديث القيد المحاسبي رقم ${id} بداية التنفيذ`);
    console.log(`عدد الأسطر المرسلة: ${entryData.lines?.length || 0}`);
    
    // إضافة معلومات خاصة بالتحديث
    const updatedEntryData = {
      ...entryData,
      updateType: 'FULL_UPDATE', // إشارة واضحة لنوع التحديث
      timestamp: new Date().toISOString(), // طابع زمني للمساعدة في تتبع التحديثات
    };
    
    // طلب التحديث مع بيانات إضافية لتتبع التنفيذ
    const response = await apiService.put<{ id: string; entryNumber: string; message: string }>(
      `/finance/journal-entries/${id}`,
      updatedEntryData,
      {
        headers: {
          'X-Update-Type': 'FULL_UPDATE',
          'X-Client-Timestamp': new Date().toISOString()
        }
      }
    );
    
    console.log(`تم تحديث القيد المحاسبي رقم ${id} بنجاح`);
    return response;
  } catch (error) {
    console.error(`خطأ في تحديث القيد المحاسبي رقم ${id}:`, error);
    throw error;
  }
};

export const deleteJournalEntry = async (id: string) => {
  try {
    return await apiService.delete<{ message: string }>(`/finance/journal-entries/${id}`);
  } catch (error) {
    console.error(`Error deleting journal entry ${id}:`, error);
    throw error;
  }
};

export const approveJournalEntry = async (id: string) => {
  try {
    return await apiService.post<{ id: string; status: string; message: string }>(`/finance/journal-entries/${id}/approve`);
  } catch (error) {
    console.error(`Error approving journal entry ${id}:`, error);
    throw error;
  }
};

export const rejectJournalEntry = async (id: string) => {
  try {
    return await apiService.post<{ id: string; status: string; message: string }>(`/finance/journal-entries/${id}/reject`);
  } catch (error) {
    console.error(`Error rejecting journal entry ${id}:`, error);
    throw error;
  }
};

// Account Services
export const getAccounts = async (params?: any) => {
  try {
    return await apiService.get<Account[]>('/finance/accounts', { params });
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }

};

// Voucher Template Services
export const getVoucherTemplates = async (params?: { templateType?: string; isActive?: boolean }) => {
  try {
    return await apiService.get<VoucherTemplate[]>('/finance/voucher-templates', { params });
  } catch (error) {
    console.error('Error fetching voucher templates:', error);
    throw error;
  }
};

export const getVoucherTemplateById = async (id: string) => {
  try {
    return await apiService.get<VoucherTemplate>(`/finance/voucher-templates/${id}`);
  } catch (error) {
    console.error(`Error fetching voucher template ${id}:`, error);
    throw error;
  }
};

export const createVoucherTemplate = async (templateData: Partial<VoucherTemplate>) => {
  try {
    return await apiService.post<{ message: string; template: VoucherTemplate }>('/finance/voucher-templates', templateData);
  } catch (error) {
    console.error('Error creating voucher template:', error);
    throw error;
  }
};

export const updateVoucherTemplate = async (id: string, templateData: Partial<VoucherTemplate>) => {
  try {
    return await apiService.put<{ message: string; template: VoucherTemplate }>(`/finance/voucher-templates/${id}`, templateData);
  } catch (error) {
    console.error(`Error updating voucher template ${id}:`, error);
    throw error;
  }
};

export const deleteVoucherTemplate = async (id: string) => {
  try {
    return await apiService.delete<{ message: string }>(`/finance/voucher-templates/${id}`);
  } catch (error) {
    console.error(`Error deleting voucher template ${id}:`, error);
    throw error;
  }
};

// تعريف نوع استجابة المعاملات الجديد
export interface TransactionsResponse {
  transactions: Transaction[];
  totalCount?: number;
  message?: string;
  code?: string;
}

// Transaction Services
// Basic transaction fetching
export const getTransactions = async (params?: {
  accountId?: string;
  startDate?: string;
  endDate?: string;
  transactionType?: string;
  search?: string;
  showRealOnly?: boolean;
}): Promise<TransactionsResponse | Transaction[]> => {
  try {
    return await apiService.get<TransactionsResponse | Transaction[]>('/finance/transactions', { params });
  } catch (error: any) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

// Safe getter for transactions that handles any API response format
export const getSafeTransactions = async (params?: {
  accountId?: string;
  startDate?: string;
  endDate?: string;
  transactionType?: string;
  search?: string;
  showRealOnly?: boolean;
}): Promise<TransactionsResponse> => {
  try {
    const response = await getTransactions(params);
    
    // Handle different response formats
    if (Array.isArray(response)) {
      return {
        transactions: response,
        totalCount: response.length
      };
    } else {
      // Response is already TransactionsResponse
      return response as TransactionsResponse;
    }
  } catch (error: any) {
    console.error('Error in getSafeTransactions:', error);
    // Return an empty result with error message
    return {
      transactions: [],
      totalCount: 0,
      message: error.message
    };
  }
};

export const getTransactionById = async (id: string) => {
  try {
    return await apiService.get<Transaction>(`/finance/transactions/${id}`);
  } catch (error) {
    console.error(`Error fetching transaction ${id}:`, error);
    return null; // Return null instead of throwing to prevent UI crashes
  }
};

// Format functions to transform data between frontend and backend
// Get real account transactions (including general ledger, receipt vouchers, and payment vouchers)
export const getRealAccountTransactions = async (params?: {
  accountId?: string;
  startDate?: string;
  endDate?: string;
  transactionType?: string;
  search?: string;
}): Promise<TransactionsResponse> => {
  try {
    // Always set showRealOnly to true to ensure we only get real transactions
    const queryParams = { ...params, showRealOnly: true };
    return await getSafeTransactions(queryParams);
  } catch (error) {
    console.error('Error fetching real account transactions:', error);
    throw error;
  }
};

/**
 * التحقق من وجود معاملات مالية لفواتير متعددة (مع التحقق المتقدم)
 */
export const checkInvoicesHaveTransactions = async (invoiceNumbers: string[]): Promise<{
  success: boolean;
  data: Record<string, {
    hasTransactions: boolean;
    count: number;
    totalDebit: number;
    totalCredit: number;
    transactionTypes: string[];
    verification: {
      status: 'complete' | 'partial' | 'missing' | 'no_transactions';
      invoiceTotal: number;
      paymentTransactionsTotal: number;
      amountMatch: boolean;
      customerSupplierRequired: boolean;
      customerSupplierExists: boolean;
      customerSupplierMatch: boolean;
      paymentTransactionsCount: number;
      customerSupplierTransactionsCount: number;
    };
  }>;
}> => {
  try {
    return await apiService.post('/finance/transactions/check-invoices', { invoiceNumbers });
  } catch (error) {
    console.error('Error checking invoices transactions:', error);
    throw error;
  }

};

/**
 * التحقق من وجود معاملات مالية لسندات القبض والصرف
 */
export const checkVouchersHaveTransactions = async (
  voucherNumbers: string[],
  voucherType: 'receipt' | 'payment'
): Promise<{
  success: boolean;
  results: Record<string, {
    status: 'complete' | 'partial' | 'missing';
    voucherAmount: number;
    debitAmount: number;
    creditAmount: number;
    transactionsCount: number;
    expectedTransactions: number;
    debitAccount: string;
    creditAccount: string;
    hasDebitTransaction: boolean;
    hasCreditTransaction: boolean;
    issues: string[] | null;
  }>;
}> => {
  try {
    return await apiService.post('/finance/transactions/check-vouchers', {
      voucherNumbers,
      voucherType
    });
  } catch (error) {
    console.error('Error checking vouchers transactions:', error);
    throw error;
  }

};

/**
 * التحقق من وجود معاملات مالية للقيود المحاسبية
 */
export const checkJournalEntriesHaveTransactions = async (entryNumbers: string[]): Promise<{
  success: boolean;
  results: Record<string, {
    status: 'complete' | 'partial' | 'missing';
    entryStatus: string;
    linesCount: number;
    transactionsCount: number;
    entryDebitTotal: number;
    entryCreditTotal: number;
    transactionsDebitTotal: number;
    transactionsCreditTotal: number;
    debitMatch: boolean;
    creditMatch: boolean;
    issues: string[] | null;
  }>;
}> => {
  try {
    return await apiService.post('/finance/transactions/check-journal-entries', { entryNumbers });
  } catch (error) {
    console.error('Error checking journal entries transactions:', error);
    throw error;
  }
};

/**
 * التحقق من وجود معاملات مالية للسندات المتخصصة
 */
export const checkSpecializedVouchersHaveTransactions = async (voucherNumbers: string[]): Promise<{
  success: boolean;
  results: Record<string, {
    status: 'complete' | 'partial' | 'missing';
    voucherAmount: number;
    debitAmount: number;
    creditAmount: number;
    transactionsCount: number;
    expectedTransactions: number;
    debitAccount: string;
    creditAccount: string;
    hasDebitTransaction: boolean;
    hasCreditTransaction: boolean;
    isApproved: boolean;
    issues: string[] | null;
  }>;
}> => {
  try {
    return await apiService.post('/finance/transactions/check-specialized-vouchers', { voucherNumbers });
  } catch (error) {
    console.error('Error checking specialized vouchers transactions:', error);
    throw error;
  }
};

// Get specific transaction types (Receipt Vouchers)
export const getReceiptVouchers = async (params?: {
  accountId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}): Promise<TransactionsResponse> => {
  try {
    // استخدام النقطة النهائية المتخصصة لسندات القبض
    const response = await apiService.get<TransactionsResponse | Transaction[]>('/finance/transactions/receipts', {
      params: params
    });
    
    // تحويل الاستجابة إلى التنسيق المطلوب
    if (response && typeof response === 'object' && 'transactions' in response) {
      return response as TransactionsResponse;
    } else if (Array.isArray(response)) {
      const transactionArray = response as Transaction[];
      return {
        transactions: transactionArray,
        totalCount: transactionArray.length
      };
    } else {
      console.warn('Unexpected response format from receipt transactions API:', response);
      return {
        transactions: [],
        totalCount: 0,
        message: 'تنسيق استجابة غير متوقع من API'
      };
    }
  } catch (error: any) {
    console.error('Error fetching receipt vouchers:', error);
    // إرجاع كائن استجابة فارغ مع رسالة خطأ للعرض في واجهة المستخدم
    return {
      transactions: [],
      totalCount: 0,
      message: error.message || 'حدث خطأ أثناء جلب بيانات سندات القبض. يرجى المحاولة مرة أخرى.'
    };
  }
};

// Get specific transaction types (Payment Vouchers)
export const getPaymentVouchers = async (params?: {
  accountId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}): Promise<TransactionsResponse> => {
  try {
    // استخدام النقطة النهائية المتخصصة لسندات الصرف
    const response = await apiService.get<TransactionsResponse | Transaction[]>('/finance/transactions/payments', {
      params: params
    });
    
    // تحويل الاستجابة إلى التنسيق المطلوب
    if (response && typeof response === 'object' && 'transactions' in response) {
      return response as TransactionsResponse;
    } else if (Array.isArray(response)) {
      const transactionArray = response as Transaction[];
      return {
        transactions: transactionArray,
        totalCount: transactionArray.length
      };
    } else {
      console.warn('Unexpected response format from payment transactions API:', response);
      return {
        transactions: [],
        totalCount: 0,
        message: 'تنسيق استجابة غير متوقع من API'
      };
    }
  } catch (error: any) {
    console.error('Error fetching payment vouchers:', error);
    // إرجاع كائن استجابة فارغ مع رسالة خطأ للعرض في واجهة المستخدم
    return {
      transactions: [],
      totalCount: 0,
      message: error.message || 'حدث خطأ أثناء جلب بيانات سندات الصرف. يرجى المحاولة مرة أخرى.'
    };
  }
};

// Get general ledger entries
export const getGeneralLedgerEntries = async (params?: {
  accountId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}): Promise<TransactionsResponse> => {
  try {
    const queryParams = {
      ...params,
      transactionType: 'قيد يومية',  // Journal entry
      showRealOnly: true
    };
    
    return await getSafeTransactions(queryParams);
  } catch (error) {
    console.error('Error fetching general ledger entries:', error);
    throw error;
  }
};

// Get account balance
export const getAccountBalance = async (accountId: string): Promise<number> => {
  try {
    const response = await apiService.get<{ balance: number }>(`/finance/accounts/${accountId}/balance`);
    return response.balance;
  } catch (error) {
    console.error(`Error fetching balance for account ${accountId}:`, error);
    throw error;
  }
};

export const formatJournalEntryForApi = (formData: any): JournalEntry => {
  // التشخيص والتتبع
  console.log('بيانات النموذج قبل التنسيق للإرسال:', JSON.stringify(formData));
  
  // التحويل من تنسيق واجهة المستخدم إلى تنسيق الواجهة البرمجية بإضافة بيانات خاصة بتعديل الأسطر
  const formattedData = {
    date: formData.date,
    description: formData.description,
    status: formData.status,
    isEditing: !!formData.id, // إضافة علامة لتوضيح أن هذا تعديل وليس إنشاء
    
    // تنسيق خاص للأسطر مع إضافة معلومات إضافية لكل سطر
    lines: formData.lines.map((line: any, index: number) => {
      // تحويل القيم إلى أرقام مع التعامل مع القيم الفارغة
      const debit = line.debit === '' ? 0 : Number(line.debit);
      const credit = line.credit === '' ? 0 : Number(line.credit);
      
      // التأكد من وجود رقم سطر (lineNumber) وإذا لم يكن موجوداً نستخدم الفهرس + 1
      const lineNumber = line.lineNumber || index + 1;
      
      return {
        id: line.id,
        lineNumber: lineNumber, // إضافة رقم السطر المطلوب في نموذج الخادم
        index: index, // إضافة فهرس السطر للمساعدة في الخادم
        rowKey: `row-${index}-${line.id}`, // إضافة مفتاح فريد لكل سطر
        accountId: line.accountId,
        description: line.description || '',
        debit: debit,
        credit: credit
      };
    })
  };
  
  // التشخيص والتتبع المحسن
  console.log('البيانات بعد التنسيق للإرسال:', JSON.stringify(formattedData));
  console.log(`عدد الأسطر المرسلة: ${formattedData.lines.length}`);
  formattedData.lines.forEach((line, i) => {
    console.log(`تفاصيل السطر ${i}:`, JSON.stringify(line));
  });
  
  return formattedData;
};

// Get transactions for a specific account with filtering
export const getAccountTransactions = async (accountId: string, params?: {
  startDate?: string;
  endDate?: string;
  transactionType?: string;
  search?: string;
  showRealOnly?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}): Promise<{
  success?: boolean;
  account: Account;
  data?: Transaction[];
  transactions?: Transaction[];
  total?: number;
  totalCount?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  totals: { totalDebit: number; totalCredit: number };
  error?: string;
}> => {
  try {
    console.log(`Attempting to fetch account transactions: /finance/transactions/account/${accountId} with params:`, params);
    
    const result = await apiService.get<{
      success: boolean;
      account: Account;
      data: Transaction[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      totals: { totalDebit: number; totalCredit: number };
    }>(`/finance/transactions/account/${accountId}`, { params });
    
    console.log('API Response for account transactions:', result);
    
    // Support both old and new response formats
    return {
      ...result,
      transactions: result.data || [],
      totalCount: result.total || 0
    };
  } catch (error: any) {
    console.error(`Error fetching transactions for account ${accountId}:`, error);
    console.error('Full error details:', error.response?.data || error.message);
    // بدلاً من رمي الخطأ، نعيد كائن نتيجة مع معلومات الخطأ
    return {
      account: {
        id: accountId,
        name: 'غير متاح',
        accountNumber: 'غير متاح',
        currentBalance: 0
      } as Account,
      transactions: [],
      totalCount: 0,
      totals: { totalDebit: 0, totalCredit: 0 },
      error: error.response?.data?.message || 'حدث خطأ في تحميل معاملات الحساب'
    };
  }
};

// Confirm transaction balance
export const confirmTransactionBalance = async (transactionId: string) => {
  try {
    return await apiService.patch<{ success: boolean; message: string; data: any }>(
      `/finance/transactions/confirm-balance/${transactionId}`
    );
  } catch (error) {
    console.error(`Error confirming transaction balance ${transactionId}:`, error);
    throw error;
  }
};